import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, concatMap, shareReplay } from 'rxjs/operators';
import {
  AuthAccessTokenDetails,
  DashboardResponse,
  LoginOAuthParams,
  OauthAEMConfig,
  PkceCode,
  RedirectfulResponse
} from '../../../../models/login';
import { LoginRememberMeUtilsService } from '../../../../utils/login';
import { CookieHelperService, LoggingService } from '../../../utils';
import { LoginRememberMeWrapperService } from '../../wrappers/login-remember-me-wrapper.service';
import { UserDataService } from './user-data/user-data.service';

@Injectable({
  providedIn: 'root'
})
export class UserSessionHelperExtendedService {
  protected userData = inject(UserDataService);
  protected loginRememberMeWrapper = inject(LoginRememberMeWrapperService);
  protected logging = inject(LoggingService);
  protected loginRememberMeUtils = inject(LoginRememberMeUtilsService);
  protected cookieHelper = inject(CookieHelperService);
  protected data$!: Observable<DashboardResponse>;
  protected sessionRequests = new Map<string, Observable<DashboardResponse>>();

  pkceCode = {} as PkceCode;
  tokenAvailable = false;
  tokenExpiry = 0;
  tokenIssuedTime = 0;
  tokenValue$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  removeSessionValues(): void {
    if (
      window.localStorage.getItem('uid') ||
      window.localStorage.getItem('encyToken') ||
      this.cookieHelper.getCookieValue('accountType')
    ) {
      window.localStorage.removeItem('uid');
      window.localStorage.removeItem('encyToken');
      this.cookieHelper.setCookieName('accountType', '', 0, false);
    }
  }

  registerServiceWorker(response: OauthAEMConfig): void {
    if (!('serviceWorker' in navigator)) return;

    const origin = window.location.origin;
    const modifiedOrigin = origin.replace(/^https?:\/\//, '');
    const scope = modifiedOrigin.split('/')[0];

    if (response?.ENABLE_NEW_PERSISTENT_LOGIN) {
      this.registerNewServiceWorker(response, scope);
    } else {
      this.unregisterServiceWorkers();
    }
  }

  registerNewServiceWorker(response: OauthAEMConfig, scope: string): void {
    if (!response.REGISTER_SW_URL) return;

    navigator.serviceWorker
      .register(response.REGISTER_SW_URL, {
        scope: `https://${scope}`,
        updateViaCache: 'none'
      })
      .then(() => navigator.serviceWorker.ready)
      .catch((e) =>
        this.logging.log(`Service worker registration failed: ${e}`)
      );
  }

  unregisterServiceWorkers(): void {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => {
        registration.unregister();
        this.logging.log('Service workers have been unregistered');
        window.location.reload();
      });
    });
  }

  async guestStatus(): Promise<void> {
    if (this.loginRememberMeUtils.checkServiceWorkerController()) {
      await this.loginRememberMeUtils.sendMessage({
        type: 'ADD_STATUS',
        eamStatus: 'GUEST'
      });
    }
  }

  nonLoggedInStateHelper(error?: string): Observable<DashboardResponse> {
    this.guestStatus();
    this.removeSessionValues();
    this.tokenValue$.next(true);
    if (error) {
      this.logging.log(error);
    }
    return of({} as DashboardResponse);
  }

  getTokenAndUserSessionData(
    loginOAuthParams: LoginOAuthParams
  ): Observable<DashboardResponse> {
    return this.loginRememberMeWrapper
      .initiatePingFlow(
        loginOAuthParams,
        this.loginRememberMeUtils.isRbaEnabledBasedOnApp(
          loginOAuthParams.oauthConfig
        )
          ? 'INITIATE_LOGIN_RBA'
          : 'INITIATE_LOGIN'
      )
      .pipe(
        concatMap(
          (
            response:
              | AuthAccessTokenDetails
              | RedirectfulResponse
              | boolean
              | string
          ) => {
            if (typeof response === 'string' || typeof response === 'boolean') {
              return this.nonLoggedInStateHelper();
            } else if ('access_token' in response) {
              this.loginRememberMeUtils.sendTokenServiceWorker(
                loginOAuthParams.oauthConfig,
                response
              );
              this.tokenValue$.next(true);
              this.data$ = this.userData
                .getDashBrdData(
                  loginOAuthParams.oauthConfig,
                  response.access_token
                )
                .pipe(shareReplay(1));
              return this.data$;
            } else {
              return this.nonLoggedInStateHelper();
            }
          }
        ),
        catchError((error) => {
          return this.nonLoggedInStateHelper(error);
        })
      );
  }
}
